import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SlackLinkMethod } from '@fleet/shared';
import {
  usersAdminApi,
  type AdminUserView,
  type SlackMatchReport,
} from '../../api/users';
import { AsyncBoundary } from '../../ui/AsyncBoundary';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { DataTable } from '../../ui/DataTable';
import { ErrorNotice } from '../../ui/ErrorNotice';
import { formatInstant } from '../../ui/datetime';
import { inputClass } from '../../ui/Field';
import { ProvisionUserDialog } from './ProvisionUserDialog';
import { SlackLinkDialog } from './SlackLinkDialog';

const LINK_METHOD_LABELS: Record<SlackLinkMethod, string> = {
  [SlackLinkMethod.SELF_SERVICE_SSO]: 'selbst bestätigt',
  [SlackLinkMethod.EMAIL_LOOKUP]: 'per Mailabgleich',
  [SlackLinkMethod.ADMIN_MANUAL]: 'von Hand',
};

/**
 * Nutzerverwaltung.
 *
 * Schließt zwei Lücken, die sich im Betrieb gezeigt haben:
 *
 *  - **Niemand existiert, bevor er sich anmeldet.** Eine Person entstand bisher
 *    ausschließlich beim ersten Login; bis dahin ließ sich weder für sie buchen
 *    noch ihr etwas erlauben.
 *  - **Der Slack-Abgleich trifft nicht immer.** Bei unterschiedlichen lokalen
 *    Teilen der Mailadresse hilft auch `SLACK_EMAIL_DOMAINS` nicht weiter.
 *
 * Nur für die Administration — anders als die übrigen Reiter der Verwaltung,
 * die auch der Fuhrparkleitung offenstehen. Wer Slack-Verknüpfungen setzt,
 * entscheidet mit, wer über einen Slack-Knopf freigeben darf, und das ist eine
 * Rechteentscheidung.
 */
export function UsersPage() {
  const [term, setTerm] = useState('');
  const [provisioning, setProvisioning] = useState(false);
  const [linking, setLinking] = useState<AdminUserView | null>(null);

  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => usersAdminApi.list(),
  });

  const revoke = useMutation({
    mutationFn: (userId: string) => usersAdminApi.revokeSlackLink(userId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  const match = useMutation({
    mutationFn: () => usersAdminApi.matchSlack(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  /**
   * Gefiltert wird im Browser, obwohl der Endpunkt `?q=` kann.
   *
   * Bei einem Bestand dieser Größe kommt ohnehin alles am Stück, und ein
   * Netzaufruf je Tastendruck wäre schlechter — man sähe die Liste bei jedem
   * Buchstaben kurz leer.
   */
  const needle = term.trim().toLowerCase();
  const matches = (query.data ?? []).filter(
    (user) =>
      !needle ||
      user.displayName.toLowerCase().includes(needle) ||
      user.userPrincipalName.toLowerCase().includes(needle) ||
      (user.email ?? '').toLowerCase().includes(needle),
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Nutzer</h2>
          <p className="text-sm text-slate-500">
            Personen anlegen, bevor sie sich zum ersten Mal anmelden, und Slack-Konten von Hand
            verknüpfen, wenn die Mailadressen nicht zusammenpassen.
          </p>
        </div>
        <div className="flex gap-2">
          {/*
            Der Abgleich ist eine ausdrückliche Handlung und kein Nebeneffekt des
            Seitenaufrufs: Er legt Verknüpfungen an, und die entscheiden mit
            darüber, wer später über Slack freigeben darf.
          */}
          <Button
            variant="secondary"
            disabled={match.isPending}
            onClick={() => match.mutate()}
            title="Gleicht die Mailadressen aller Personen ohne Verknüpfung mit dem Slack-Workspace ab"
          >
            {match.isPending ? 'Wird abgeglichen …' : 'Slack abgleichen'}
          </Button>
          <Button variant="primary" onClick={() => setProvisioning(true)}>
            Nutzer anlegen
          </Button>
        </div>
      </div>

      <input
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        placeholder="Nach Name oder Mailadresse suchen"
        className={inputClass}
      />

      {revoke.isError && <ErrorNotice error={revoke.error} />}
      {match.isError && <ErrorNotice error={match.error} />}
      {match.data && <MatchReport report={match.data} />}

      <AsyncBoundary query={query} empty="Noch niemand eingetragen.">
        {() =>
          matches.length === 0 ? (
            <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
              Niemand passt zu „{term}".
            </div>
          ) : (
            <DataTable
              rows={matches}
              rowKey={(user) => user.id}
              rowClassName={(user) => (user.isActive ? '' : 'opacity-60')}
              columns={[
                {
                  header: 'Name',
                  cell: (user) => (
                    <div>
                      <div className="font-medium">{user.displayName}</div>
                      <div className="text-xs text-slate-500">
                        {user.email ?? user.userPrincipalName}
                      </div>
                    </div>
                  ),
                },
                { header: 'Status', cell: (user) => <UserState user={user} /> },
                { header: 'Slack', cell: (user) => <SlackState user={user} /> },
                {
                  header: 'Letzter Login',
                  cell: (user) =>
                    user.lastLoginAt ? (
                      <span className="text-xs tabular-nums">
                        {formatInstant(user.lastLoginAt, 'medium')}
                      </span>
                    ) : (
                      <span className="text-slate-400">nie</span>
                    ),
                },
                {
                  header: '',
                  align: 'right',
                  cell: (user) => (
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="secondary" onClick={() => setLinking(user)}>
                        {user.slackLink && !user.slackLink.revokedAt ? 'Slack ändern' : 'Slack verknüpfen'}
                      </Button>
                      {user.slackLink && !user.slackLink.revokedAt && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={revoke.isPending}
                          onClick={() => {
                            if (
                              confirm(
                                `Slack-Verknüpfung von ${user.displayName} widerrufen?\n\n` +
                                  'Danach kommen keine Slack-Nachrichten mehr an, und über Slack ' +
                                  'kann nichts mehr freigegeben werden. Der automatische ' +
                                  'Mailabgleich verknüpft nicht von selbst neu.',
                              )
                            ) {
                              revoke.mutate(user.id);
                            }
                          }}
                        >
                          Lösen
                        </Button>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          )
        }
      </AsyncBoundary>

      {provisioning && <ProvisionUserDialog onClose={() => setProvisioning(false)} />}
      {linking && <SlackLinkDialog user={linking} onClose={() => setLinking(null)} />}
    </div>
  );
}

/**
 * „Noch nie angemeldet" ist die Angabe, auf die es hier ankommt: Genau das sind
 * die Zeilen, die über diesen Reiter entstanden sind. Sie sagt, dass die Person
 * eingerichtet ist, aber noch nichts von ihrem Zugang weiß.
 */
function UserState({ user }: { user: AdminUserView }) {
  if (!user.isActive) return <Badge tone="red">deaktiviert</Badge>;
  if (!user.lastLoginAt) return <Badge tone="blue">noch nie angemeldet</Badge>;
  return <Badge tone="emerald">aktiv</Badge>;
}

/**
 * Ergebnis des Abgleichs.
 *
 * Zeigt bewusst **Namen und geprüfte Adressen** und nicht nur Zahlen: „3 ohne
 * Treffer" lässt offen, wer das ist und woran es lag. Mit den probierten
 * Adressen daneben sieht man auf einen Blick, ob die Mailadresse in Slack
 * abweicht oder ob `SLACK_EMAIL_DOMAINS` eine Domain vermisst — und kann das
 * eine von Hand verknüpfen, das andere konfigurieren.
 */
function MatchReport({ report }: { report: SlackMatchReport }) {
  const nothingHappened =
    report.linked.length === 0 &&
    report.unmatched.length === 0 &&
    report.guests.length === 0 &&
    report.conflicts.length === 0;

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4 text-sm">
      <p className="font-medium">
        {report.linked.length} neu verknüpft
        {report.skipped.length > 0 && `, ${report.skipped.length} schon verknüpft oder widerrufen`}
      </p>

      {nothingHappened && report.skipped.length > 0 && (
        <p className="text-slate-500">Es war nichts offen — alle Konten sind bereits geklärt.</p>
      )}

      {report.linked.length > 0 && (
        <ul className="space-y-0.5 text-xs">
          {report.linked.map((entry) => (
            <li key={entry.slackUserId}>
              <Badge tone="emerald">neu</Badge>{' '}
              <span className="font-medium">{entry.displayName}</span>{' '}
              <span className="text-slate-500">
                → {entry.email ?? entry.slackUserId} ({entry.slackUserId})
              </span>
            </li>
          ))}
        </ul>
      )}

      {report.unmatched.length > 0 && (
        <div>
          <p className="text-xs font-medium text-slate-700">
            Ohne Treffer — diese Adressen wurden geprüft:
          </p>
          <ul className="mt-1 space-y-0.5 text-xs">
            {report.unmatched.map((entry) => (
              <li key={entry.displayName}>
                <span className="font-medium">{entry.displayName}</span>{' '}
                <span className="font-mono text-slate-500">{entry.tried.join(', ') || '—'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {report.guests.length > 0 && (
        <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
          <p className="font-medium">Gäste — nicht automatisch verknüpft:</p>
          <ul className="mt-1 space-y-0.5">
            {report.guests.map((entry) => (
              <li key={entry.slackUserId}>
                {entry.displayName} → {entry.slackName} ({entry.slackUserId})
              </li>
            ))}
          </ul>
          <p className="mt-1">
            Ein Gastkonto gehört nicht regulär zur Organisation. Wer darüber freigeben können soll,
            wird von Hand verknüpft.
          </p>
        </div>
      )}

      {report.conflicts.length > 0 && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-900">
          <p className="font-medium">Konflikte — das Slack-Konto gehört schon jemandem:</p>
          <ul className="mt-1 space-y-0.5">
            {report.conflicts.map((entry) => (
              <li key={`${entry.displayName}-${entry.slackUserId}`}>
                {entry.displayName} → {entry.slackUserId} (belegt von {entry.belongsTo})
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SlackState({ user }: { user: AdminUserView }) {
  const link = user.slackLink;
  if (!link) return <span className="text-slate-400">—</span>;

  if (link.revokedAt) {
    return (
      <span title={`Widerrufen am ${formatInstant(link.revokedAt)}`}>
        <Badge tone="neutral">widerrufen</Badge>
      </span>
    );
  }

  return (
    <div>
      <Badge tone="emerald">verknüpft</Badge>
      <div className="mt-0.5 font-mono text-xs text-slate-500">{link.slackUserId}</div>
      <div className="text-xs text-slate-400">{LINK_METHOD_LABELS[link.verifiedVia]}</div>
    </div>
  );
}
